import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import type { CrmObjectTypeSchema } from '@/lib/crm/core/SchemaClient';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from '@/lib/crm/hooks/useEntityTypeSchema';
import { toKebabSlug } from '@/lib/crm/utils/entityTypeUtils';
import {
  registerEntityTypeIcons,
  registerEntityTypeKinds,
} from '@/lib/crm/utils/icons';
import {
  getPersistedObjectTypes,
  setPersistedObjectTypes,
} from '@/lib/localStorage/persistedObjectTypes';

// Types with hardcoded nav entries — excluded from the custom-entity bucket so
// they are never rendered twice. ahoy_task is case-kind, so without this entry
// the nav auto-files it beside Deals under CRM as if it were a custom entity.
const SYSTEM_CRM_TYPES = new Set([
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
  'ahoy_note',
  'ahoy_task',
]);

export interface NavObjectTypeItem {
  entity_type_name: string;
  label: string;
  label_plural: string;
  icon: string | null | undefined;
  slug: string;
  // Ontology kind(s) — used by the nav to group custom entities (assets, cases).
  entity_kind: string[];
}

export function useObjectTypes(orgId: number | undefined) {
  // Canonical shared key — every consumer of /crm-events/schemas/object-types/
  // dedupes through this cache. See useEntityTypeSchema.ts. This hook is the
  // only canonical-key consumer that supplies initialData from localStorage,
  // so when it mounts first (typical: SideNavigation), it implicitly seeds the
  // shared cache for sibling consumers — a UX win.
  const query = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId as number),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    initialData: () => getPersistedObjectTypes(orgId)?.allObjectTypes,
    initialDataUpdatedAt: () => 0,
  });

  const allObjectTypes = query.data;

  const items = useMemo<NavObjectTypeItem[]>(() => {
    if (!allObjectTypes) {
      return [];
    }
    return allObjectTypes
      .filter(
        t =>
          t.allows_custom_properties &&
          !SYSTEM_CRM_TYPES.has(t.entity_type_name) &&
          // `*_timeline` entity type names are not valid object-type
          // identifiers in product surfaces — they alias the underlying
          // entity type and surfacing them in navigation duplicates the
          // canonical entry. Match the pattern used by other consumers
          // (e.g. proposals/registry.helpers, MixedActionTable).
          !t.entity_type_name.endsWith('_timeline') &&
          !t.entity_kind.includes('pipeline') &&
          !t.entity_kind.includes('stage')
      )
      .map(t => ({
        entity_type_name: t.entity_type_name,
        label: t.label,
        label_plural: t.label_plural,
        icon: t.icon,
        slug: toKebabSlug(t.entity_type_name),
        entity_kind: t.entity_kind,
      }));
  }, [allObjectTypes]);

  // Preserve the existing { items, allObjectTypes } consumer shape via shim.
  // 12 importers read query.data.items or query.data.allObjectTypes.
  const shimmedData = useMemo(() => {
    if (!allObjectTypes) {
      return undefined;
    }
    return { items, allObjectTypes };
  }, [items, allObjectTypes]);

  useEffect(() => {
    if (!allObjectTypes?.length) {
      return;
    }
    registerEntityTypeIcons(
      Object.fromEntries(
        allObjectTypes
          .filter((t): t is CrmObjectTypeSchema & { icon: string } =>
            Boolean(t.icon)
          )
          .map(t => [t.entity_type_name, t.icon])
      )
    );
    registerEntityTypeKinds(
      Object.fromEntries(
        allObjectTypes.map(t => [t.entity_type_name, t.entity_kind ?? []])
      )
    );
  }, [allObjectTypes]);

  useEffect(() => {
    if (!orgId) {
      return;
    }
    if (!shimmedData) {
      return;
    }
    setPersistedObjectTypes(orgId, shimmedData);
  }, [shimmedData, orgId]);

  return { ...query, data: shimmedData };
}
